import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Components
import RoomListCard from '../list/RoomListCard';
import FilterWidget from '../../../components/FilterWidget';
import { SearchField, ListNote, Pager, paginate, sameRows } from '../../../components/ListControls';

// Constants
import { FilterOptions } from '../../../constants';
import { T } from '../../../constants/tokens';

/** "Room 2" before "Room 10". */
const byIdentifier = (a, b) => (a.getIdentifier() ?? '').localeCompare(b.getIdentifier() ?? '', undefined, { numeric: true });

/**
 * The rooms on the floor the map is showing, under the map.
 *
 * It is given the map's own rooms rather than fetching the school's, so the
 * list and the plan above it are always the same floor, and choosing a room
 * here selects that same room on the map. It used to list every room in the
 * school, which put rooms in the list that were nowhere on the plan above.
 */
export default function RoomList({ rooms: floorRooms, floorLabel, selectedRoomID, onSelect }) {
  const [filtered, setFiltered] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const lastFiltered = useRef(null);

  const rooms = floorRooms == null ? null : [...floorRooms].sort(byIdentifier);

  // A different floor is a different list: start from its first page, with
  // no filter result carried over from the last one.
  useEffect(() => {
    setPage(1);
    setFiltered(null);
    lastFiltered.current = null;
  }, [floorLabel]);

  // The filter hands back the rooms it was given a moment ago; show the
  // current copies of those, so a status change is not a refresh behind.
  const keep = filtered == null ? null : new Set(filtered.map((room) => room.getID()));
  const source = rooms == null || keep == null ? rooms : rooms.filter((room) => keep.has(room.getID()));
  const needle = search.trim().toLowerCase();
  const visible = source == null
    ? null
    : source.filter((room) => !needle || (room.getIdentifier() ?? '').toLowerCase().includes(needle));
  const { page: current, pageCount, pageRows } = paginate(visible ?? [], page);
  const selected = rooms?.find((room) => room.getID() === selectedRoomID) ?? null;

  return (
    <section style={{ marginTop: 48 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 32, fontWeight: 600, lineHeight: '40px', letterSpacing: '-1.28px', color: T.ink }}>
          Rooms
        </h2>
        <span style={{ fontSize: 14, lineHeight: '20px', color: T.body }}>
          {visible == null
            ? 'Choose a floor to list its rooms'
            : (visible.length === 1 ? '1 room' : visible.length + ' rooms') + (floorLabel ? ' on ' + floorLabel : ' on this floor')}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <SearchField
          value={search}
          label='search rooms'
          placeholder='Search rooms'
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <FilterWidget
          filterOptions={FilterOptions.ROOM_LIST}
          data={rooms ?? []}
          updateFilteredData={(data) => {
            // Back to page 1 when the filter changes what is listed, not on
            // every refresh that re-runs it.
            if (!sameRows(lastFiltered.current, data, (room) => room.getID())) setPage(1);
            lastFiltered.current = data;
            setFiltered(data);
          }}
        />
      </div>

      {visible != null && visible.length === 0 &&
        <ListNote>{rooms?.length ? 'Nothing matches the current search and filters.' : 'No rooms on this floor yet.'}</ListNote>}
      {pageRows.map((room) => (
        <RoomListCard
          key={room.getID()}
          room={room}
          selectedCardObject={selected}
          onClick={() => onSelect(room)}
        />
      ))}

      <Pager page={current} pageCount={pageCount} onChange={setPage} />
    </section>
  );
}

RoomList.propTypes = {
  /** The rooms of the floor on the map; null until a floor has loaded. */
  rooms: PropTypes.array,
  /** e.g. "Floor 1, Main Building", for the count line. */
  floorLabel: PropTypes.string,
  selectedRoomID: PropTypes.number,
  onSelect: PropTypes.func.isRequired
};
